import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNotification } from '../NotificationContext';

const CategoryManagerModal = ({ isOpen, onClose, onUpdate }) => {
    const { addNotification } = useNotification();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const pageSize = 5;

    // Edit/Add state
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editActive, setEditActive] = useState(true);
    const [isNew, setIsNew] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const res = await api.get('/expense-categories');
            setCategories(res.data || []);
        } catch (err) {
            addNotification('โหลดหมวดหมู่ไม่สำเร็จ', 'danger');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadCategories();
            setPage(1);
            resetForm();
        }
    }, [isOpen]);

    const resetForm = () => {
        setEditingId(null);
        setEditName('');
        setEditActive(true);
        setIsNew(false);
    };

    const handleEdit = (cat) => {
        setEditingId(cat.id);
        setEditName(cat.name);
        setEditActive(cat.is_active);
        setIsNew(false);
    };

    const handleAddNew = () => {
        setEditingId('new');
        setEditName('');
        setEditActive(true);
        setIsNew(true);
    };

    const handleSave = async () => {
        if (!editName.trim()) {
            addNotification('กรุณาระบุชื่อหมวดหมู่', 'warning');
            return;
        }

        try {
            setSaving(true);
            if (isNew) {
                await api.post('/expense-categories', { name: editName });
                addNotification('เพิ่มหมวดหมู่สำเร็จ', 'success');
            } else {
                await api.put(`/expense-categories/${editingId}`, {
                    name: editName,
                    is_active: editActive
                });
                addNotification('บันทึกการแก้ไขสำเร็จ', 'success');
            }
            await loadCategories();
            resetForm();
            onUpdate?.(); // Notify parent to reload dropdown
        } catch (err) {
            const msg = err.response?.data?.message || 'บันทึกไม่สำเร็จ';
            addNotification(msg, 'danger');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`ต้องการลบหมวดหมู่ "${name}" ใช่หรือไม่?`)) return;
        try {
            setSaving(true);
            await api.delete(`/expense-categories/${id}`);
            addNotification('ลบหมวดหมู่สำเร็จ', 'success');
            await loadCategories();
            onUpdate?.();
        } catch (err) {
            const msg = err.response?.data?.message || 'ลบไม่สำเร็จ';
            addNotification(msg, 'danger');
        } finally {
            setSaving(false);
        }
    };

    // Pagination logic
    const totalPages = Math.ceil(categories.length / pageSize);
    const displayedCategories = categories.slice((page - 1) * pageSize, page * pageSize);

    if (!isOpen) return null;

    return (
        <>
            <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="bi bi-tags me-2"></i>จัดการหมวดหมู่รายจ่าย
                            </h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            {/* Add New Button */}
                            {!editingId && (
                                <button className="btn btn-sm btn-success mb-3" onClick={handleAddNew}>
                                    <i className="bi bi-plus-circle me-1"></i>เพิ่มหมวดหมู่
                                </button>
                            )}

                            {/* Edit Form */}
                            {editingId && (
                                <div className="card mb-3 bg-light">
                                    <div className="card-body p-3">
                                        <h6 className="card-title mb-2">{isNew ? 'เพิ่มหมวดหมู่ใหม่' : 'แก้ไขหมวดหมู่'}</h6>
                                        <div className="mb-2">
                                            <label className="form-label small text-muted">ชื่อหมวดหมู่</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={editName}
                                                onChange={e => setEditName(e.target.value)}
                                                placeholder="ระบุชื่อหมวดหมู่..."
                                                disabled={saving}
                                            />
                                        </div>
                                        {!isNew && (
                                            <div className="form-check form-switch mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="catActiveSwitch"
                                                    checked={editActive}
                                                    onChange={e => setEditActive(e.target.checked)}
                                                    disabled={saving}
                                                />
                                                <label className="form-check-label small" htmlFor="catActiveSwitch">
                                                    ใช้งาน (Active)
                                                </label>
                                            </div>
                                        )}
                                        <div className="d-flex gap-2 justify-content-end">
                                            <button className="btn btn-sm btn-secondary" onClick={resetForm} disabled={saving}>ยกเลิก</button>
                                            <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                                                {saving ? 'บันทึก...' : 'บันทึก'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* List */}
                            <div className="table-responsive">
                                <table className="table table-sm table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>ชื่อหมวดหมู่</th>
                                            <th style={{ width: '80px' }}>สถานะ</th>
                                            <th style={{ width: '100px' }} className="text-end">จัดการ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan={3} className="text-center py-3">กำลังโหลด...</td></tr>
                                        ) : displayedCategories.length === 0 ? (
                                            <tr><td colSpan={3} className="text-center py-3 text-muted">ไม่มีข้อมูล</td></tr>
                                        ) : (
                                            displayedCategories.map(cat => (
                                                <tr key={cat.id}>
                                                    <td>{cat.name}</td>
                                                    <td>
                                                        {cat.is_active ? (
                                                            <span className="badge bg-success-subtle text-success rounded-pill">Active</span>
                                                        ) : (
                                                            <span className="badge bg-secondary-subtle text-secondary rounded-pill">Inactive</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <button className="btn btn-sm btn-link text-primary p-0 me-2" onClick={() => handleEdit(cat)}>
                                                            <i className="bi bi-pencil-square"></i>
                                                        </button>
                                                        <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDelete(cat.id, cat.name)}>
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center gap-2 mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        disabled={page <= 1}
                                        onClick={() => setPage(p => p - 1)}
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                    <span className="d-flex align-items-center small text-muted">
                                        หน้า {page} / {totalPages}
                                    </span>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage(p => p + 1)}
                                    >
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>ปิด</button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop show"></div>
        </>
    );
};

export default CategoryManagerModal;
